// public/js/setting/settingAccessControl.js
import api from '@services/api.js';
import { createLogger } from '@utils/logger.js';

const logger = createLogger('SettingAccessControl');

/**
 * Setting Access Control (Client-Side)
 * Checks if modules are enabled before allowing navigation
 */
class SettingAccessControl {
  constructor() {
    if (SettingAccessControl.instance) return SettingAccessControl.instance;
    SettingAccessControl.instance = this;

    this.enabledModules = null; // Cache enabled modules
    this.loading = false;
    this.loadPromise = null;
  }

  /**
   * Module name mapping from routes to settings
   */
  ROUTE_TO_MODULE = {
    'dogs': 'dog',
    'activities': 'activity',
    'results': 'result',
    'notes': 'note',
    'files': 'file',
    'parcours': 'parcour',
    'weights': 'weight',
    'profile': 'user'
  };

  /**
   * Load enabled modules from API
   * @returns {Promise<Object>} Enabled modules object
   */
  async loadEnabledModules() {
    // Return cached value if available
    if (this.enabledModules) {
      return this.enabledModules;
    }

    // If already loading, wait for existing promise
    if (this.loading && this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.loading = true;
    this.loadPromise = this._fetchEnabledModules();

    try {
      this.enabledModules = await this.loadPromise;
      return this.enabledModules;
    } finally {
      this.loading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Internal method to fetch enabled modules from API
   * @returns {Promise<Object>}
   */
  async _fetchEnabledModules() {
    try {
      // Fetch all user settings
      const response = await api.apiCall('/api/settings', 'GET');

      if (!response.success || !response.data) {
        logger.warn('Failed to fetch settings, assuming all enabled');
        return this._getDefaultEnabled();
      }

      // Build enabled modules object
      const enabledModules = this._getDefaultEnabled();

      // Parse settings data
      if (Array.isArray(response.data)) {
        response.data.forEach(setting => {
          if (setting.module_name && setting.settings_data) {
            try {
              const settingsData = typeof setting.settings_data === 'string'
                ? JSON.parse(setting.settings_data)
                : setting.settings_data;

              if (typeof settingsData.enabled === 'boolean') {
                enabledModules[setting.module_name] = settingsData.enabled;
              }
            } catch (error) {
              logger.error({ module: setting.module_name, error }, 'Failed to parse settings data');
            }
          }
        });
      }

      logger.debug({ enabledModules }, 'Enabled modules loaded');
      return enabledModules;

    } catch (error) {
      logger.error({ error }, 'Error fetching enabled modules');
      return this._getDefaultEnabled(); // Fail open
    }
  }

  /**
   * Get default enabled modules (all enabled)
   * @returns {Object}
   */
  _getDefaultEnabled() {
    return {
      dog: true,
      activity: true,
      result: true,
      note: true,
      file: true,
      parcour: true,
      weight: true,
      user: true
    };
  }

  /**
   * Extract module name from route
   * @param {string} route - Route path (e.g., "/dashboard/dogs" or "#/dashboard/dogs")
   * @returns {string|null} Module name or null
   */
  extractModuleFromRoute(route) {
    // Normalize route
    const normalized = route.replace(/^#?\/?/, '');
    const parts = normalized.split('/');

    // Handle dashboard routes
    if (parts[0] === 'dashboard' && parts[1]) {
      const routeSegment = parts[1];
      return this.ROUTE_TO_MODULE[routeSegment] || null;
    }

    return null;
  }

  /**
   * Check if a route is accessible (module is enabled)
   * @param {string} route - Route to check
   * @returns {Promise<boolean>} True if accessible
   */
  async isRouteAccessible(route) {
    // Extract module from route
    const moduleName = this.extractModuleFromRoute(route);

    // If no module mapping (e.g., /dashboard, /dashboard/trash, /dashboard/settings), always allow
    if (!moduleName) {
      return true;
    }

    // Load enabled modules
    const enabledModules = await this.loadEnabledModules();

    // Check if module is enabled
    const isEnabled = enabledModules[moduleName] !== false;

    if (!isEnabled) {
      logger.info({ route, moduleName }, 'Route blocked - module disabled');
    }

    return isEnabled;
  }

  /**
   * Invalidate cache (call after settings are updated)
   */
  invalidateCache() {
    this.enabledModules = null;
    logger.debug('Settings cache invalidated');
  }

  /**
   * Pre-load enabled modules on app startup (optional optimization)
   */
  async preload() {
    try {
      await this.loadEnabledModules();
      logger.info('Enabled modules pre-loaded');
    } catch (error) {
      logger.error({ error }, 'Failed to pre-load enabled modules');
    }
  }
}

const settingAccessControl = new SettingAccessControl();
export default settingAccessControl;
